import React, { useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Download, Loader2, AlertCircle } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Quita tildes y deja sólo [A-Za-z0-9] para que el nombre sea seguro en
 * Windows/macOS/Linux (los apellidos con ñ o tildes rompían el archivo en
 * algunos navegadores al ir por `a.download`).
 */
const slug = (s) => String(s || '')
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .replace(/[^A-Za-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

/**
 * "Laura Pérez González" → "Laura-Perez": primer nombre + primer apellido.
 * El nombre completo trae hasta 3–4 palabras (nombres compuestos + 2 apellidos)
 * y producía archivos como "Lau-Lau-Lau". Nos quedamos con las 2 primeras
 * palabras útiles, que es lo que identifica a la persona de un vistazo.
 */
export function nombreCorto(nombreCompleto) {
  const partes = String(nombreCompleto || '').trim().split(/\s+/).filter(Boolean);
  return partes.slice(0, 2).map(slug).filter(Boolean).join('-');
}

/**
 * Nombre del PDF descargado: persona + abreviación del plan.
 * Ej. "trazabilidad-cmf-Laura-Perez-C20050.pdf". Cada parte se omite si falta,
 * y si no queda nada se cae al id del contratante para no generar ".pdf" pelado.
 */
export function buildNombreArchivo({ nombreContratante, planCodigo, contratanteId }) {
  const partes = ['trazabilidad-cmf', nombreCorto(nombreContratante), slug(planCodigo)]
    .filter(Boolean);
  // Sólo el prefijo => no había ni nombre ni código utilizables; el id evita
  // que dos descargas distintas caigan en el mismo archivo.
  if (partes.length === 1) partes.push(slug(contratanteId) || 'sin-datos');
  return `${partes.join('-')}.pdf`;
}

const TrazabilidadModal = ({ contratanteId, nombreContratante, open, onClose }) => {
  // Token de sesión de Clerk: identifica al usuario del admin en el backend
  // (auditoría) y reemplaza el secret compartido que antes iba en el bundle.
  const { getToken } = useAuth();
  const [planes, setPlanes]           = useState([]);
  const [planActivo, setPlanActivo]   = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [pdfLoading, setPdfLoading]   = useState(false);
  const [pdfError, setPdfError]       = useState(null);

  // 1. Al abrir, cargar la lista de planes
  useEffect(() => {
    if (!open || !contratanteId) return;
    setPlanes([]);
    setPlanActivo(null);
    setPreviewHtml('');
    setError(null);

    (async () => {
      try {
        const token = await getToken({ skipCache: true });
        const res = await fetch(`/api/pdf/trazabilidad-planes?id=${contratanteId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j?.error || `Error ${res.status}`);
        }
        const { planes: p } = await res.json();
        setPlanes(p || []);
        // Seleccionar el plan actual (esActual) o el primero
        const inicial = (p || []).find(pl => pl.esActual) || p?.[0] || null;
        setPlanActivo(inicial?.contratacion_id || null);
      } catch (e) {
        setError(e.message);
      }
    })();
  }, [open, contratanteId, getToken]);

  // 2. Al cambiar el plan activo, cargar el HTML del iframe
  useEffect(() => {
    if (!planActivo) return;
    setPreviewHtml('');
    setError(null);
    setLoading(true);

    (async () => {
      try {
        const token = await getToken({ skipCache: true });
        const res = await fetch(`/api/pdf/preview?type=trazabilidad&id=${planActivo}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j?.error || `Error ${res.status}`);
        }
        setPreviewHtml(await res.text());
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [planActivo, getToken]);

  const handleDownloadPdf = async () => {
    if (!planActivo) return;
    setPdfLoading(true);
    setPdfError(null);
    try {
      // Los JWT de sesión de Clerk viven ~60s. Si el modal estuvo abierto un
      // rato, el token pudo vencer justo al descargar → 401/403. Forzamos un
      // token recién emitido (skipCache) y reintentamos una vez si aún así falla
      // la auth, para eliminar la ventana de expiración.
      const pedir = async () => {
        const token = await getToken({ skipCache: true });
        return fetch('/api/pdf/generate', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body:    JSON.stringify({ type: 'trazabilidad', id: planActivo, action: 'download' }),
        });
      };

      let res = await pedir();
      if (res.status === 401 || res.status === 403) {
        res = await pedir(); // reintento único con token renovado
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Error ${res.status}`);
      }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = buildNombreArchivo({
        nombreContratante,
        planCodigo: planes.find(pl => pl.contratacion_id === planActivo)?.planCodigo,
        contratanteId,
      });
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setPdfError(e.message);
    } finally {
      setPdfLoading(false);
    }
  };

  const tieneMultiplesPlanes = planes.length > 1;
  // Con un solo plan no hay nada que "cambiar", pero igual conviene mostrar qué
  // plan es y qué certificado tiene (antes quedaba oculto). Se resuelve el plan
  // a mostrar en la cabecera única a partir del activo.
  const planUnico = !tieneMultiplesPlanes
    ? (planes.find(pl => pl.contratacion_id === planActivo) || planes[0] || null)
    : null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <div className="flex items-center justify-between pr-6">
            <div>
              <DialogTitle className="text-lg">Trazabilidad CMF</DialogTitle>
              <p className="text-sm text-muted-foreground mt-0.5">{nombreContratante}</p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Button
                size="sm"
                variant="outline"
                disabled={pdfLoading || loading || !!error || !planActivo}
                onClick={handleDownloadPdf}
              >
                {pdfLoading
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <Download className="h-4 w-4 mr-2" />}
                {pdfLoading ? 'Generando…' : 'Exportar PDF'}
              </Button>
              {pdfError && <p className="text-xs text-destructive">{pdfError}</p>}
            </div>
          </div>
        </DialogHeader>

        {/* Varios planes: tabs para cambiar entre ellos. Cada una muestra el
            plan y, debajo, su código de certificado (o "sin certificado"). */}
        {tieneMultiplesPlanes && (
          <div className="flex gap-1 border-b border-border px-0 pb-0 -mb-px">
            {planes.map((plan) => (
              <button
                key={plan.contratacion_id}
                onClick={() => setPlanActivo(plan.contratacion_id)}
                className={cn(
                  'px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap text-left',
                  planActivo === plan.contratacion_id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                )}
              >
                <span className="block">{plan.planNombre}</span>
                <span className="block text-xs font-normal opacity-70">
                  {plan.certificadoCodigo || 'Sin certificado'}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Un solo plan: no hay nada que cambiar, pero se muestra igual cuál es
            y qué certificado tiene (antes esta info quedaba oculta). */}
        {!tieneMultiplesPlanes && planUnico && (
          <div className="flex items-center gap-2 border-b border-border pb-2 -mb-px text-sm">
            <span className="font-medium text-foreground">{planUnico.planNombre}</span>
            {planUnico.productoNombre && (
              <span className="text-muted-foreground">· {planUnico.productoNombre}</span>
            )}
            <span
              className={cn(
                'ml-auto rounded-full px-2 py-0.5 text-xs font-medium',
                planUnico.certificadoCodigo
                  ? 'bg-primary/10 text-primary'
                  : 'bg-muted text-muted-foreground'
              )}
            >
              {planUnico.certificadoCodigo
                ? `Certificado ${planUnico.certificadoCodigo}`
                : 'Sin certificado'}
            </span>
          </div>
        )}

        <div className="flex-1 min-h-0 pt-2 flex flex-col">
          {loading && (
            <div className="flex items-center justify-center h-64 gap-3 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
              Cargando trazabilidad…
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-destructive py-8">
              <AlertCircle className="h-5 w-5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {previewHtml && (
            <iframe
              srcDoc={previewHtml}
              title="Trazabilidad CMF"
              className="w-full h-full rounded-lg border border-slate-200 bg-white"
              sandbox="allow-same-origin"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TrazabilidadModal;
